/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * DATA ACCESS LAYER : Monteur Actions
 * ═══════════════════════════════════════════════════════════════════════════════
 * 
 * Gère les actions spécifiques au Monteur : téléchargement des sources Derush
 * et upload des vidéos montées vers le dossier Montage S3.
 * 
 * ═══════════════════════════════════════════════════════════════════════════════
 */

import { supabase } from "@/integrations/supabase/client";
import { findShootingFolder, createVirtualFile, extractIdFromFolderName } from "./rushesVirtualFolders";
import { uploadFileToS3 } from "./s3Files";

interface DerushFile {
  id: string;
  display_name: string;
  s3_key: string;
  file_size: number | null;
}

/**
 * Fetches a subfolder by parent folder ID and folder name.
 */
async function fetchSubfolderByParent(parentFolderId: string, folderName: string) {
  const { data } = await supabase
    .from("rushes_virtual_folders")
    .select("id, name")
    .eq("parent_folder_id", parentFolderId)
    .eq("name", folderName)
    .maybeSingle();
  return data;
}

/**
 * Attempts to find a subfolder by matching video content ID in folder names.
 */
async function findSubfolderByVideoContentId(
  topicFolders: { id: string; name: string }[],
  videoContentId: string,
  folderName: string
) {
  const shortVcId = videoContentId.replaceAll('-', '').slice(0, 8).toLowerCase();

  const matchById = topicFolders.find(f => {
    const embedded = extractIdFromFolderName(f.name);
    return embedded?.toLowerCase() === shortVcId;
  });

  if (!matchById) return null;
  return fetchSubfolderByParent(matchById.id, folderName);
}

/**
 * Attempts to find a subfolder by matching video content title.
 */
async function findSubfolderByTitle(
  topicFolders: { id: string; name: string }[],
  videoContentId: string,
  folderName: string
) {
  const { data: vc } = await supabase
    .from("video_content")
    .select("title")
    .eq("id", videoContentId)
    .single();

  if (!vc?.title) return null;

  const matchingTopic = topicFolders.find(f => f.name === vc.title);
  if (!matchingTopic) return null;

  return fetchSubfolderByParent(matchingTopic.id, folderName);
}

/**
 * Trouve un sous-dossier (Derush/Montage) dans un shooting.
 * Structure: Shooting > Topic > {Derush, Montage}
 * On cherche dans tous les sous-dossiers topic du shooting.
 */
export async function getSubfolder(shootingId: string, folderName: string, videoContentId?: string) {
  const shootingFolder = await findShootingFolder(shootingId);
  if (!shootingFolder) return null;

  // Get all topic subfolders of the shooting
  const { data: topicFolders } = await supabase
    .from("rushes_virtual_folders")
    .select("id, name")
    .eq("parent_folder_id", shootingFolder.id);

  if (!topicFolders || topicFolders.length === 0) return null;

  // If we have a videoContentId, try to match by embedded ID first, then by title
  if (videoContentId) {
    const resultById = await findSubfolderByVideoContentId(topicFolders, videoContentId, folderName);
    if (resultById) return resultById;

    const resultByTitle = await findSubfolderByTitle(topicFolders, videoContentId, folderName);
    if (resultByTitle) return resultByTitle;
  }

  // Fallback: search all topic folders for the target subfolder
  const topicIds = topicFolders.map(f => f.id);
  const { data } = await supabase
    .from("rushes_virtual_folders")
    .select("id, name")
    .in("parent_folder_id", topicIds)
    .eq("name", folderName)
    .limit(1)
    .maybeSingle();

  return data;
}

/**
 * Récupère les fichiers du dossier Derush d'un shooting
 */
export async function getDerushFiles(shootingId: string): Promise<DerushFile[]> {
  const folder = await getSubfolder(shootingId, "Derush");
  if (!folder) return [];

  const { data } = await supabase
    .from("rushes_virtual_files")
    .select("id, display_name, s3_key, file_size")
    .eq("folder_id", folder.id)
    .order("display_name");

  return (data as DerushFile[]) || [];
}

/**
 * Upload une vidéo montée vers le dossier Montage S3 et met à jour video_content
 */
export async function uploadMonteurVideo(
  shootingId: string,
  videoContentId: string,
  file: File,
  videoSlot: "video_1" | "video_2" | "video_3",
  onProgress?: (progress: number) => void
): Promise<{ success: boolean; error?: string; data?: { key: string } }> {
  try {
    // 1. Trouver le dossier Montage
    const montageFolder = await getSubfolder(shootingId, "Montage");
    if (!montageFolder) {
      return { success: false, error: "Dossier Montage introuvable" };
    }

    // 2. Générer la clé S3
    const timestamp = Date.now();
    const randomId = Math.random().toString(36).substring(2, 8);
    const s3Key = `uploads/montage/${timestamp}-${randomId}/${file.name}`;

    // 3. Upload vers S3
    const uploadResult = await uploadFileToS3(file, s3Key, onProgress);
    if (!uploadResult.success) {
      return { success: false, error: uploadResult.error || "Échec de l'upload" };
    }

    // 4. Créer l'entrée fichier virtuel
    await createVirtualFile(
      s3Key,
      file.name,
      montageFolder.id,
      file.size,
      new Date().toISOString()
    );

    // 5. Mettre à jour video_content avec la clé S3
    const urlField = `${videoSlot}_url` as const;
    const { error } = await supabase
      .from("video_content")
      .update({ [urlField]: s3Key } as any)
      .eq("id", videoContentId);

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true, data: { key: s3Key } };
  } catch (err) {
    console.error("[monteurActions] uploadMonteurVideo error:", err);
    return { success: false, error: err instanceof Error ? err.message : "Erreur inconnue" };
  }
}

/**
 * Passe le statut d'une vidéo en editing_review ou redelivery_review selon le statut actuel
 * - editing_pending → editing_review (première livraison)
 * - client_feedback → redelivery_review (relivraison après retour client, en attente VE)
 */
export async function transitionToEditingReview(
  videoContentId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    // Fetch current status to determine correct target
    const { data: video } = await supabase
      .from("video_content")
      .select("status")
      .eq("id", videoContentId)
      .single();

    const targetStatus = video?.status === 'client_feedback' ? 'redelivery_review' : 'editing_review';

    const { error } = await supabase
      .from("video_content")
      .update({ status: targetStatus } as any)
      .eq("id", videoContentId);

    if (error) {
      return { success: false, error: error.message };
    }
    return { success: true };
  } catch (err) {
    console.error("[monteurActions] transitionToEditingReview error:", err);
    return { success: false, error: err instanceof Error ? err.message : "Erreur inconnue" };
  }
}
